import {AppLanguage, fallbackLanguage, knownLocales} from '@lib/utils';
import {createSlice} from '@reduxjs/toolkit';
import {startOfToday} from 'date-fns';
import {
  changeAppLanguage,
  loadHasUserClosedNotificationReminder,
  loadSettings,
  loadUserSettings,
  loadWasDeskBookingOnboardingShown,
  loadWasUserOnboardedToNotifications,
  markDeskBookingOnboardingAsShown,
  markPushNotificationReminderAsClosed,
  markUserAsOnboardedToNotifications,
  setAppLanguage,
  setStartOfToday,
} from './actions';
import {SettingsState} from './types';

const initialState: SettingsState = {
  settings: {
    companyName: '',
    companyWebsite: '',
    contactEmailAddress: '',
    effectivePrivacyPolicyUrl: '',
    defaultAccessProfile: '',
    defaultLocationProfile: '',
    officeInstructions: '',
    registrationPolicy: 'CreateSameDayDeleteBeforeMidnight',
    invitationExpireDays: 3,
    maxNumberOfGroupWorkspaceInvitees: 30,
    userRetentionPeriodDays: 30,
    shiftsRetentionPeriodDays: 600,
    parking: {checkDuplicateLicensePlates: false, licensePlateIsRequired: false},
  },
  isLoading: false,
  supportLink: 'https://support.mapiq.com/',
  eulaLink: 'https://www.mapiq.com/eula-office-shifts-app',
  appLanguage: fallbackLanguage,
  appLanguages: <AppLanguage[]>Object.keys(knownLocales),
  isChangingAppLanguage: false,
  wasDeskBookingOnboardingShown: false,
  isDeskBookingOnboardingLoaded: false,
  hasUserClosedPushNotificationReminder: false,
  isUserClosedPushNotificationReminderLoaded: false,
  startOfTodayISOString: startOfToday().toISOString(),
  wasUserOnboardedToNotifications: false,
  faqLink: 'https://support.mapiq.com/support/solutions/35000142390',
  userSettings: {
    loadingStatus: 'NotLoaded',
    settings: {
      accessControl: undefined,
    },
  },
};

const slice = createSlice({
  name: 'settings',
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadSettings.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loadSettings.fulfilled, (state, action) => {
        state.settings = {
          ...action.payload,
          effectivePrivacyPolicyUrl: action.payload.effectivePrivacyPolicyUrl || 'https://www.mapiq.com/privacy-policy',
        };
        state.isLoading = false;
      })
      .addCase(loadSettings.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(setAppLanguage, (state, action) => {
        state.appLanguage = action.payload;
      })
      .addCase(changeAppLanguage.pending, (state) => {
        state.isChangingAppLanguage = true;
      })
      .addCase(changeAppLanguage.fulfilled, (state, action) => {
        state.isChangingAppLanguage = false;
        state.appLanguage = action.payload;
      })
      .addCase(changeAppLanguage.rejected, (state) => {
        state.isChangingAppLanguage = false;
      })
      .addCase(loadWasDeskBookingOnboardingShown.fulfilled, (state, action) => {
        state.wasDeskBookingOnboardingShown = action.payload;
        state.isDeskBookingOnboardingLoaded = true;
      })
      .addCase(markDeskBookingOnboardingAsShown.fulfilled, (state) => {
        state.wasDeskBookingOnboardingShown = true;
      })
      .addCase(loadWasUserOnboardedToNotifications.fulfilled, (state, action) => {
        state.wasUserOnboardedToNotifications = action.payload;
      })
      .addCase(markUserAsOnboardedToNotifications.fulfilled, (state) => {
        state.wasUserOnboardedToNotifications = true;
      })
      .addCase(setStartOfToday, (state, action) => {
        if (state.startOfTodayISOString === action.payload) return;

        state.startOfTodayISOString = action.payload;
      })
      .addCase(loadHasUserClosedNotificationReminder.fulfilled, (state, action) => {
        state.hasUserClosedPushNotificationReminder = action.payload;
        state.isUserClosedPushNotificationReminderLoaded = true;
      })
      .addCase(markPushNotificationReminderAsClosed.fulfilled, (state) => {
        state.hasUserClosedPushNotificationReminder = true;
      })
      .addCase(loadUserSettings.pending, (state) => {
        state.userSettings.loadingStatus = 'Loading';
      })
      .addCase(loadUserSettings.fulfilled, (state, action) => {
        state.userSettings.settings = action.payload;
        state.userSettings.loadingStatus = 'Loaded';
      })
      .addCase(loadUserSettings.rejected, (state) => {
        state.userSettings.loadingStatus = 'Failed';
      });
  },
});

export const {reducer: settingsReducer} = slice;
